import React, { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement | string, options: Record<string, any>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

interface TurnstileProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  siteKey?: string;
  resetKey?: number | string;
}

export function Turnstile({ onVerify, onExpire, onError, siteKey: propSiteKey, resetKey }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [siteKey, setSiteKey] = useState<string>(propSiteKey || '');

  useEffect(() => {
    if (propSiteKey) {
      setSiteKey(propSiteKey);
      return;
    }
    fetch('/api/public-config')
      .then(res => res.json())
      .then(data => {
        if (data && data.turnstileSiteKey) {
          setSiteKey(data.turnstileSiteKey);
        } else {
          setSiteKey('1x00000000000000000000AA');
        }
      })
      .catch(() => {
        setSiteKey('1x00000000000000000000AA');
      });
  }, [propSiteKey]);

  useEffect(() => {
    if (!siteKey) return;

    const renderWidget = () => {
      if (window.turnstile && containerRef.current) {
        if (widgetIdRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {}
          widgetIdRef.current = null;
        }

        try {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token: string) => {
              onVerify(token);
            },
            'expired-callback': () => {
              onExpire?.();
            },
            'error-callback': () => {
              onError?.();
            },
            theme: 'light',
            execution: 'render',
          });
        } catch (e) {
          console.error('Turnstile render error:', e);
        }
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      let script = document.getElementById('cf-turnstile-script') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.id = 'cf-turnstile-script';
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          renderWidget();
        };
        document.head.appendChild(script);
      } else {
        const interval = setInterval(() => {
          if (window.turnstile) {
            clearInterval(interval);
            renderWidget();
          }
        }, 100);
        return () => clearInterval(interval);
      }
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, resetKey]);

  return <div ref={containerRef} className="my-3 flex justify-center" />;
}
